import { winningOutcomeMotif, type GameListItem, type WinningOutcomeMotif } from "@/features/games";
import { isChessSquare, type ChessSquare, type ChessSquareStyles } from "@/shared/components/chessboard";

type WinGame = Pick<
  GameListItem,
  "id" | "opponent" | "playerColor" | "result" | "analyzed" | "playedAt" | "termination"
>;
type FinalPosition = {
  moves: Array<{ fenAfter: string; playedFrom?: string; playedTo?: string; san?: string; motifs?: string[] }>;
};

export interface LatestWinMove {
  from: ChessSquare;
  to: ChessSquare;
  san: string;
  checkmate: boolean;
}

export interface LatestWinBoardState {
  fen: string;
  gameId: string | null;
  lastMove: LatestWinMove | null;
  opponent: string | null;
  orientation: "white" | "black";
  outcomeMotif: WinningOutcomeMotif | null;
  playedAt: string | null;
}

const FALLBACK: LatestWinBoardState = {
  fen: "start",
  gameId: null,
  lastMove: null,
  opponent: null,
  orientation: "white",
  outcomeMotif: null,
  playedAt: null,
};

const LAST_MOVE_HIGHLIGHT = "rgba(255, 255, 51, 0.5)";

export const lastMoveSquareStyles = (move: LatestWinMove | null): ChessSquareStyles =>
  move
    ? {
        [move.from]: { backgroundColor: LAST_MOVE_HIGHLIGHT },
        [move.to]: { backgroundColor: LAST_MOVE_HIGHLIGHT },
      }
    : {};

export const latestAnalyzedWin = <T extends WinGame>(games: T[]): T | null =>
  games
    .filter((game) => game.result === "win" && game.analyzed)
    .sort((a, b) => Date.parse(b.playedAt) - Date.parse(a.playedAt))[0] ?? null;

export const latestWinBoard = (game: WinGame | null, analysis: FinalPosition | null): LatestWinBoardState => {
  const finalMove = analysis?.moves.at(-1);
  const fen = finalMove?.fenAfter;
  if (!game || !fen) return FALLBACK;
  const lastMove =
    isChessSquare(finalMove.playedFrom) && isChessSquare(finalMove.playedTo) && finalMove.san
      ? {
          from: finalMove.playedFrom,
          to: finalMove.playedTo,
          san: finalMove.san,
          checkmate: finalMove.motifs?.includes("checkmate") === true || finalMove.san.endsWith("#"),
        }
      : null;
  return {
    fen,
    gameId: game.id,
    lastMove,
    opponent: game.opponent,
    orientation: game.playerColor === "b" ? "black" : "white",
    outcomeMotif: lastMove?.checkmate ? "checkmate" : winningOutcomeMotif(game.result, game.termination),
    playedAt: game.playedAt,
  };
};
